import { Action, Reducer } from 'redux';
import { STATE } from 'app/constants';
import {
  ACTION, RequestCodeItems, ReceiveCodeItems, FailCodeItems,
  ExpandCodeItem, UpdateSearchModel, ClearList,
  RequestCodetableMetadata, ReceiveCodetableMetadata, FailCodetableMetadata,
  ModifyItem,
  ValidationChange,
  SetCurrentCode, ClearCurrentCode,
  UpdateCodetable, UpdateCategory, UpdateImportSet, ClearAll,
  RequestImportSetData, ReceiveImportSetData, FailImportSetData,
  RequestCodetableData, ReceiveCodetableData, FailCodetableData,
  ClearMetadata,
  RequestCategoryOptions,
  ReceiveCategoryOptions,
  FailCategoryOptions,
  RequestSaveItemChanges,
  ReceiveSaveItemChanges,
  FailSaveItemChanges,
  ClearValidation,
  UpdateCodeListBranch
} from './action-types';
import { defaultIncludeInactiveCodes } from './constants';
import { compareStateWithRequestedProps, findModifiedItem } from './selectors';
import { ImportMappingsListState, ImportMappingsOptions } from './types-store';
import { withOptionsReducer } from 'app/components/options-handler';
import { deepEquals } from 'app/infrastructure/js';
import { initializeImportMappings } from 'app/area/import-mappings/list-items/modify-import-code';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

export const INITIAL_CODETABLE_LIST_STATE: ImportMappingsListState = {
  codetable: undefined,
  importSet: undefined,
  itemAccess: undefined,
  category: undefined,
  fetchingCodetableId: undefined,
  fetchingItemAccess: undefined,
  fetchingCodetableItemAccess: undefined,
  includeInactive: defaultIncludeInactiveCodes,
  codeListBranchState: [],
  codeListBranch: [],
  codetableOptions: {},
  options: {},
  requestedSearchModel: undefined,
  fetchingCodetableMetadata: false,
  codetableMetadata: undefined,
  fetchingImportSetData: false,
  fetchingCodetableData: false,
  updating: false,
  numberOfResults: undefined,
  modifiedItems: [],
  currentCode: undefined,
  validation: {}
};

const reducers: { [indexer: string]: Reducer<ImportMappingsListState, Action> } = {

  [ACTION.REQUEST_CODE_ITEMS]: (state: ImportMappingsListState, action: RequestCodeItems): ImportMappingsListState => {

    const { codetableId, parentId, categoryId, searchModel } = action;

    const compare = compareStateWithRequestedProps(state, codetableId, categoryId, searchModel);

    if (!compare) {
      return {
        ...state,
        fetchingCodetableId: codetableId,
        codeListBranchState: [
          {
            id: parentId,
            fetching: true,
            open: parentId === undefined
          }
        ],
        codeListBranch: []
      };
    }

    const currentBranchState = state.codeListBranchState.find(s => s.id === parentId) || { open: false };
    return {
      ...state,
      codeListBranchState: [
        ...state.codeListBranchState.filter(s => s !== currentBranchState),
        {
          ...currentBranchState,
          id: parentId,
          fetching: true,
          open: parentId === undefined || currentBranchState.open
        }
      ]
    };
  },

  [ACTION.RECEIVE_CODE_ITEMS]: (state: ImportMappingsListState, action: ReceiveCodeItems): ImportMappingsListState => {

    const { parentId, codeItems, numberOfResults, clearList } = action;

    const codeListBranchState = state.codeListBranchState || [];
    const codeListBranch = state.codeListBranch || [];

    const currentBranchState = codeListBranchState.find(s => s.id === parentId) ||
      { id: parentId, fetching: false };
    const currentBranch = codeListBranch.find(branch => branch.id === parentId);
    const currentCodeItems = currentBranch?.items || [];

    return {
      ...state,
      codeListBranchState: [
        ...codeListBranchState.filter(s => s !== currentBranchState),
        {
          ...currentBranchState,
          fetching: false,
          needsReload: false
        }
      ],
      codeListBranch: [
        ...codeListBranch.filter(branch => branch.id !== parentId),
        {
          id: parentId,
          numberOfResults,
          items: clearList ?
            codeItems
            : [
              ...currentCodeItems,
              ...codeItems
            ]
        }
      ]
    };
  },

  [ACTION.FAIL_CODE_ITEMS]: (state: ImportMappingsListState, action: FailCodeItems): ImportMappingsListState => {

    const { codetableId, parentId, categoryId, searchModel: query } = action;

    const currentBranchState = state.codeListBranchState.find(s => s.id === parentId) ||
      { id: parentId, fetching: false };
    const currentBranch = state.codeListBranch.find(branch => branch.id === parentId) ||
      { id: parentId, numberOfResults: 0, items: [] };

    return currentBranchState.fetching && compareStateWithRequestedProps(state, codetableId, categoryId, query)
      ? {
        ...state,
        codeListBranchState: [
          ...state.codeListBranchState.filter(s => s !== currentBranchState),
          {
            ...currentBranchState,
            fetching: false,
            needsReload: false
          }
        ],
        codeListBranch: [
          ...state.codeListBranch.filter(branch => branch !== currentBranch),
          currentBranch
        ]
      }
      : state;
  },

  [ACTION.EXPAND_CODE_ITEM]: (state: ImportMappingsListState, action: ExpandCodeItem): ImportMappingsListState => {

    const currentBranchState = state.codeListBranchState.find(s => s.id === action.parentId) ||
      { id: action.parentId, open: false };

    const newData = [
      ...state.codeListBranchState.filter(s => s !== currentBranchState),
      {
        ...currentBranchState,
        open: !currentBranchState.open
      }
    ];

    return {
      ...state,
      codeListBranchState: newData
    };
  },

  [ACTION.UPDATE_CODE_LIST_BRANCH]: (state: ImportMappingsListState, action: UpdateCodeListBranch): ImportMappingsListState => {

    return {
      ...state,
      codeListBranch: [
        ...state.codeListBranch?.map(branch => {
          return {
            ...branch,
            items: branch.items.map(item => {

              return {
                ...item,
                importMapping: item.id === state.currentCode.id
                  ? state.currentCode.importMapping.filter(mapping => isDefinedIncludingZero(mapping.importNumber))
                  : item.importMapping
              };
            })
          };
        })
      ]
    };
  },


  [ACTION.UPDATE_SEARCH_MODEL]: (state: ImportMappingsListState, action: UpdateSearchModel): ImportMappingsListState => {
    return {
      ...state,
      requestedSearchModel: action.model
    };
  },

  [ACTION.REQUEST_IMPORTSET_DATA]: (state: ImportMappingsListState, action: RequestImportSetData): ImportMappingsListState => {

    return {
      ...state,
      fetchingImportSetData: true
    };
  },

  [ACTION.RECEIVE_IMPORTSET_DATA]: (state: ImportMappingsListState, action: ReceiveImportSetData): ImportMappingsListState => {

    return {
      ...state,
      importSet: action.data,
      fetchingImportSetData: false
    };
  },

  [ACTION.FAIL_IMPORTSET_DATA]: (state: ImportMappingsListState, action: FailImportSetData): ImportMappingsListState => {

    return {
      ...state,
      fetchingImportSetData: false
    };
  },

  [ACTION.REQUEST_CODETABLE_DATA]: (state: ImportMappingsListState, action: RequestCodetableData): ImportMappingsListState => {

    return {
      ...state,
      fetchingCodetableData: true
    };
  },

  [ACTION.RECEIVE_CODETABLE_DATA]: (state: ImportMappingsListState, action: ReceiveCodetableData): ImportMappingsListState => {

    return {
      ...state,
      codetable: action.data,
      fetchingCodetableData: false
    };
  },

  [ACTION.FAIL_CODETABLE_DATA]: (state: ImportMappingsListState, action: FailCodetableData): ImportMappingsListState => {

    return {
      ...state,
      fetchingCodetableData: false
    };
  },

  [ACTION.REQUEST_CODETABLE_METADATA]: (state: ImportMappingsListState, action: RequestCodetableMetadata): ImportMappingsListState => {

    return {
      ...state,
      fetchingCodetableMetadata: true
    };
  },

  [ACTION.RECEIVE_CODETABLE_METADATA]: (state: ImportMappingsListState, action: ReceiveCodetableMetadata): ImportMappingsListState => {

    return {
      ...state,
      codetableMetadata: action.metadata,
      fetchingCodetableMetadata: false
    };
  },

  [ACTION.FAIL_CODETABLE_METADATA]: (state: ImportMappingsListState, action: FailCodetableMetadata): ImportMappingsListState => {

    return {
      ...state,
      fetchingCodetableMetadata: false
    };
  },

  [ACTION.REQUEST_CATEGORY_OPTIONS]: (state: ImportMappingsListState, action: RequestCategoryOptions): ImportMappingsListState => {
    return {
      ...state,
      options: {
        ...(state.options || {}),
        [action.id]: {
          fetching: true,
          loadOptionsParams: action.loadOptionsParams,
          codes: undefined,
          currentOptions: undefined
        }
      } as ImportMappingsOptions
    };
  },

  [ACTION.RECEIVE_CATEGORY_OPTIONS]: (state: ImportMappingsListState, action: ReceiveCategoryOptions): ImportMappingsListState => {

    if (!state.options || !state.options[action.id] ||
      !deepEquals(state.options[action.id].loadOptionsParams, action.loadOptionsParams)) {
      return state;
    }

    return {
      ...state,
      options: {
        ...state.options,
        [action.id]: {
          ...state.codetableOptions[action.id],
          fetching: false,
          codes: action.codes,
          currentOptions: action.currentOptions
        }
      } as ImportMappingsOptions
    };
  },

  [ACTION.FAIL_CATEGORY_OPTIONS]: (state: ImportMappingsListState, action: FailCategoryOptions): ImportMappingsListState => {

    if (!state.options || !state.options[action.id] ||
      !deepEquals(state.options[action.id].loadOptionsParams, action.loadOptionsParams)) {
      return state;
    }

    return {
      ...state,
      options: {
        ...state.options,
        [action.id]: {
          ...state.codetableOptions[action.id],
          fetching: false
        }
      } as ImportMappingsOptions
    };
  },

  [ACTION.CLEAR_LIST]: (
    state: ImportMappingsListState,
    action: ClearList
  ): ImportMappingsListState => {
    return {
      ...state,
      codeListBranch: [],
      codeListBranchState: [],
      modifiedItems: []
    };
  },

  [ACTION.MODIFY_ITEM]: (state: ImportMappingsListState, action: ModifyItem): ImportMappingsListState => {

    const modifiedData = action.modifiedData;
    const previousModifiedItem = findModifiedItem(state.modifiedItems, modifiedData);

    return {
      ...state,
      modifiedItems: [
        ...(state.modifiedItems || []).filter(item =>
          item !== previousModifiedItem
        ),
        modifiedData
      ]
    };
  },

  [ACTION.SET_CURRENT_CODE]: (state: ImportMappingsListState, action: SetCurrentCode): ImportMappingsListState => {

    return {
      ...state,
      currentCode: action.data
    };
  },

  [ACTION.CLEAR_CURRENT_CODE]: (state: ImportMappingsListState, action: ClearCurrentCode): ImportMappingsListState => {

    return {
      ...state,
      currentCode: undefined
    };
  },

  [ACTION.REQUEST_SAVE_ITEM_CHANGES]: (state: ImportMappingsListState, action: RequestSaveItemChanges): ImportMappingsListState => {

    return {
      ...state,
      updating: true
    };
  },

  [ACTION.RECEIVE_SAVE_ITEM_CHANGES]: (state: ImportMappingsListState, action: ReceiveSaveItemChanges): ImportMappingsListState => {

    return {
      ...state,
      modifiedItems: [],
      currentCode: initializeImportMappings(action.importNumber, state.currentCode, state.modifiedItems),
      updating: false,
      validation: {
        ...state.validation,
        importCode: {
          ...state.validation?.importCode,
          messages: state.validation?.importCode?.messages?.filter(message => message?.parameters?.importNumber !== action.importNumber)
        }
      }
    };
  },

  [ACTION.FAIL_SAVE_ITEM_CHANGES]: (state: ImportMappingsListState, action: FailSaveItemChanges): ImportMappingsListState => {

    return {
      ...state,
      modifiedItems: [],
      currentCode: initializeImportMappings(undefined, state.currentCode, state.modifiedItems, true),
      updating: false
    };
  },

  [ACTION.VALIDATION_CHANGE]: (state: ImportMappingsListState, action: ValidationChange): ImportMappingsListState => {

    return {
      ...state,
      validation: {
        ...state.validation,
        importCode: {
          ...state.validation?.importCode,
          messages: [
            ...state.validation?.importCode?.messages ?? [],
            ...action.validation?.importCode?.messages ?? []
          ]
        }
      }
    };
  },

  [ACTION.UPDATE_CODETABLE]:
    (state: ImportMappingsListState, action: UpdateCodetable): ImportMappingsListState => {

      return {
        ...state,
        codetable: action.value,
        codeListBranch: undefined,
        category: undefined
      };
    },

  [ACTION.UPDATE_CATEGORY]:
    (state: ImportMappingsListState, action: UpdateCategory): ImportMappingsListState => {

      return {
        ...state,
        category: action.value
      };
    },

  [ACTION.UPDATE_IMPORT_SET]:
    (state: ImportMappingsListState, action: UpdateImportSet): ImportMappingsListState => {

      return {
        ...state,
        importSet: action.value
      };
    },


  [ACTION.CLEAR_ALL]:
    (state: ImportMappingsListState, action: ClearAll): ImportMappingsListState => {

      return {
        ...state,
        ...INITIAL_CODETABLE_LIST_STATE
      };
    },

  [ACTION.CLEAR_VALIDATION]:
    (state: ImportMappingsListState, action: ClearValidation): ImportMappingsListState => {

      return {
        ...state,
        validation: undefined
      };
    },

  [ACTION.CLEAR_METADATA]:
    (state: ImportMappingsListState, action: ClearMetadata): ImportMappingsListState => {

      return {
        ...state,
        codetableMetadata: {
          ...state.codetableMetadata,
          [action.name]: undefined
        }
      };
    }
};

const stateReducer: Reducer<ImportMappingsListState> =
  (state: ImportMappingsListState = INITIAL_CODETABLE_LIST_STATE, action: Action) => {
    const r = reducers[action.type];
    return (r && r(state, action)) || state;
  };

export const reducer = withOptionsReducer(STATE.importMappings, stateReducer);
